/**
 * Lightweight PNG analysis for capture QA (brightness / "all black" detection).
 * Used by tests and optional runtime checks — keep dependency-free (no pngjs).
 */

/** ITU-R BT.709 luma from RGB */
export function rgbToLuma(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Sample mean luma of PNG bytes by drawing to a small canvas (browser / happy-dom).
 * Returns 0 if decode fails. For very large images, samples a grid (max ~4096 samples).
 */
export async function meanLuminanceFromPngBytes(
  png: Uint8Array,
): Promise<number> {
  if (
    typeof createImageBitmap !== "function" ||
    typeof document === "undefined"
  ) {
    return 0;
  }
  try {
    const blob = new Blob([png as unknown as BlobPart], { type: "image/png" });
    const bmp = await createImageBitmap(blob);
    const w = bmp.width;
    const h = bmp.height;
    const canvas = document.createElement("canvas");
    const step = Math.max(1, Math.floor(Math.sqrt((w * h) / 4096)));
    canvas.width = Math.ceil(w / step);
    canvas.height = Math.ceil(h / step);
    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;
    ctx.drawImage(bmp, 0, 0, w, h, 0, 0, canvas.width, canvas.height);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let sum = 0;
    const n = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      sum += rgbToLuma(data[i], data[i + 1], data[i + 2]);
    }
    bmp.close?.();
    return n > 0 ? sum / n : 0;
  } catch {
    return 0;
  }
}

/**
 * True if the image is uniformly dark (likely failed render / empty GL buffer).
 * Threshold is mean luma on 0–255 scale (default 5 ≈ pure black).
 */
export async function isPngMostlyBlack(
  png: Uint8Array,
  threshold = 5,
): Promise<boolean> {
  const m = await meanLuminanceFromPngBytes(png);
  return m < threshold;
}
